// Core packages
import { Injectable } from "@angular/core";

// Third party packages
import { Observable } from "rxjs";
import { catchError } from "rxjs/operators";

// Custom packages
import { ListApiResponseV2 } from "src/app/shared/interfaces/_base/api-response.interface";
import { ApiService } from "src/app/shared/services/api.service";
import { HelperService } from "src/app/shared/services/helper.service";

/**
 * Handles the property remappings history feature (V2 backend).
 *
 * @since 3.0.0
 */
@Injectable({
  providedIn: "root",
})
export class PropertyRemappingsService {
  /**
   * Class constructor
   */
  constructor(
    private apiService: ApiService,
    private helperService: HelperService
  ) {}

  /**
   * Get the mappable plants used to populate the remapping "plant" dropdown.
   * Labels are localized server-side based on the request language. The
   * returned keys are exactly those accepted by the create endpoint.
   *
   * @since 3.0.0
   */
  getPlants(): Observable<any> {
    return this.apiService.apiCall(
      "GET",
      "property-remappings/plants",
      {},
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Get the paginated remapping history for a property.
   *
   * @since 3.0.0
   *
   * @param propertyId Property identifier
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name (date | createdAt | plant)
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with filtering data
   */
  getList(
    propertyId: string,
    start?: number,
    limit?: number,
    sort?: string,
    sortVersus?: string,
    search?: any
  ): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach((key) => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall(
      "GET",
      `property-remappings/${propertyId}`,
      params,
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Create a new remapping record for a property. Date and mapping user are
   * derived server-side from the property mapping status; only plant and
   * description are sent.
   *
   * @since 3.0.0
   *
   * @param propertyId Property identifier
   * @param remapping Object with the plant key and the description
   */
  create(
    propertyId: string,
    remapping: { plant: string; description: string }
  ): Observable<any> {
    return this.apiService.apiCall(
      "POST",
      `property-remappings/${propertyId}`,
      remapping,
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Update an existing remapping record. Partial update: only the provided
   * fields are changed. The FE only edits plant and description; date and
   * mapping user are kept as the server-derived snapshot.
   *
   * @since 3.0.0
   *
   * @param propertyId Property identifier
   * @param remappingId Remapping record identifier
   * @param remapping Object with the fields to update
   */
  update(
    propertyId: string,
    remappingId: string,
    remapping: { plant?: string; description?: string }
  ): Observable<any> {
    return this.apiService.apiCall(
      "PATCH",
      `property-remappings/${propertyId}/${remappingId}`,
      remapping,
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Delete a remapping record.
   *
   * @since 3.0.0
   *
   * @param propertyId Property identifier
   * @param remappingId Remapping record identifier
   */
  delete(propertyId: string, remappingId: string): Observable<any> {
    return this.apiService.apiCall(
      "DELETE",
      `property-remappings/${propertyId}/${remappingId}`,
      {},
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Get an xlsx export of the whole remapping history of a property.
   *
   * @since 3.0.0
   *
   * @param propertyId Property identifier
   */
  getExport(propertyId: string): Observable<any> {
    return this.apiService
      .apiCall(
        "GET",
        `property-remappings/export/${propertyId}`,
        {},
        { responseType: "blob" },
        undefined,
        undefined,
        "v2"
      )
      .pipe(
        catchError((err) => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }
}
